import { FieldType } from '../../../types/custom-worksheet-v2';
import { NatureEnum } from '../../../types/nature';

/**
 * Get property nested level
 *
 * @param id Property id
 * @returns Nested level, 0 being at the top level
 */
function getPropertyLevelCount(id: string): number {
    return id.split('.').filter((id) => id.includes('properties')).length - 1;
}

/**
 * Calculate the left padding for a nav item
 *
 * @param level Level label for each nav item
 * @returns Total padding to add
 */
export function calcLeftPadding(id: string): number {
    const propertyNestedLevel = getPropertyLevelCount(id);
    const paddingPerLevel = 8;
    const extraPadding = 20;

    return propertyNestedLevel * paddingPerLevel + extraPadding;
}

/**
 * Calculates the height (in pixels) of the provided element to be the remaining height of the screen.
 * @param el HTML Element
 * @returns Computed nav height
 */
export function getNavHeightWithScroll(el: Element | null): number {
    const bottomPaddingOffset = 16;
    return window.innerHeight - (el?.getBoundingClientRect()?.y ?? 0) - bottomPaddingOffset;
}

/**
 * Format stored field value to a string type or an appropriate display format (e.g convert cents to dollar)
 *
 * @param field WorksheetSchemaField | CWTSchemaField
 * @param value unknown
 * @param format Whether to just convert value to string or format it
 * @returns Formatted value
 */
export function formatFieldValueToDisplay(type: FieldType, value: unknown, format = false): string | null {
    if (value == null || value === '') {
        return null;
    }
    if (format) {
        if (type === FieldType.DATE) {
            return new Date(value?.toString() || '').toISOString().substring(0, 10);
        }
        if (type === FieldType.PERCENT) {
            return Number.parseFloat((+value * 100).toFixed(4)).toString();
        }
        if (type === FieldType.MONEY) {
            return Number.parseFloat((+value / 100).toFixed(2)).toString();
        }
        if (type === FieldType.ACCOUNTING) {
            return Number.parseFloat((Math.abs(+value) / 100).toFixed(2)).toString();
        }
    }
    return value.toString();
}

/**
 * Format field to be saved in the schema (ensure value is valid/set fix decimal points)
 *
 * @param type FieldType
 * @param value Field value
 * @param dropdownValue Dropdown value (NatureEnum)
 * @returns Formatted string/number
 */
export function formatFieldValueToUpdate(type: FieldType, value: string | number, dropdownValue?: NatureEnum): string | number | null {
    let _value: string | number | null = value;

    if (_value === '') {
        return '';
    }

    if (type === FieldType.NUMBER) {
        _value = isNaN(+value) ? null : +value;
    } else if (type === FieldType.PERCENT) {
        _value = Number.parseFloat((+value / 100).toFixed(6));
    } else if (type === FieldType.MONEY) {
        _value = Number.parseFloat((+value * 100).toFixed(2));
    } else if (type === FieldType.ACCOUNTING) {
        if (dropdownValue === NatureEnum.CREDIT) {
            _value = Number.parseFloat((Math.abs(+value) * -1 * 100).toFixed(2));
        } else {
            _value = Number.parseFloat((Math.abs(+value) * 100).toFixed(2));
        }
    }

    return _value;
}

/**
 * Get offset to display the sticky property header
 * Property at the highest level will have the smallest offset value
 *
 * e.g List at the top level will have offset value of 0 -> top-0
 * e.g Section group at the first level will have offset value of 38 -> top-[38px]
 *
 * @param id Property id
 * @returns Offset value to apply on the property header
 */
export function getOffset(id: string): number {
    const propertyNestedLevel = getPropertyLevelCount(id);
    const initialOffsetValue = 0;

    // Height of each property header (38px)
    const propertySectionHeight = 38;

    return initialOffsetValue + propertyNestedLevel * propertySectionHeight;
}

/**
 * Get z-index to apply on the sticky property header
 * Property at the highest level will have the highest z-index value
 *
 * e.g List at the top level will have z-index of 40 -> z-40
 * e.g Section group at the first level will have z-index of 39 -> z-[39]
 *
 * @param id Property id
 * @returns Z-index to apply on the property header
 */
export function getZIndex(id: string): number {
    const propertyNestedLevel = getPropertyLevelCount(id);
    const initialZIndex = 40;

    return initialZIndex - propertyNestedLevel;
}

/**
 * Format field value depending on previous and new type
 *
 * PERCENT -> MONEY/ACCOUNTING = value * 10000
 * NUMBER/TEXT -> MONEY/ACCOUNTING = value * 100
 *
 * NUMBER/TEXT -> PERCENT = value / 100
 * MONEY/ACCOUNTING -> PERCENT = value / 10000
 *
 * MONEY/ACCOUNTING -> NUMBER/TEXT = value / 100
 * PERCENT -> NUMBER/TEXT = value * 100
 */
export function formatValueBasedOnTypes(value: string | number | null, prevType: FieldType, newType: FieldType): string | number | null {
    let newValue = value;
    if (MONETARY_TYPES.includes(newType)) {
        newValue = value === null ? null : Number(value) * (prevType === FieldType.PERCENT ? 10000 : 100);
    } else if (newType === FieldType.PERCENT) {
        newValue = value === null ? null : Number(value) / (MONETARY_TYPES.includes(prevType) ? 10000 : 100);
    } else {
        newValue = value === null ? null : prevType === FieldType.PERCENT ? Number(value) * 100 : Number(value) / 100;
    }
    return newValue;
}

const MONETARY_TYPES = [FieldType.MONEY, FieldType.ACCOUNTING];
const UNFORMATTED_TYPES = [FieldType.TEXT, FieldType.NUMBER];
const NON_CONVERTIBLE_TYPES = [FieldType.DATE, FieldType.DROPDOWN];

/**
 * Check if either previous or new type is a DATE/DROPDOWN type which is non-convertible
 *
 * @param prevType Previous field type
 * @param newType New field type
 * @returns True if either type is non-convertible otherwise false
 */
export function isNonConvertibleType(prevType: FieldType, newType: FieldType): boolean {
    return NON_CONVERTIBLE_TYPES.includes(prevType) || NON_CONVERTIBLE_TYPES.includes(newType);
}

/**
 * Check if either previous or new type is a MONEY/ACCOUNTING type which is monetary
 *
 * @param prevType Previous field type
 * @param newType New field type
 * @returns True if either type is monetary otherwise false
 */
export function isMonetaryType(prevType: FieldType, newType: FieldType): boolean {
    return MONETARY_TYPES.includes(prevType) && MONETARY_TYPES.includes(newType);
}

/**
 * Check if either previous or new type is a TEXT/NUMBER type which is unformatted
 *
 * @param prevType Previous field type
 * @param newType New field type
 * @returns True if either type has an unformatted value otherwise false
 */
export function isUnformattedType(prevType: FieldType, newType: FieldType): boolean {
    return UNFORMATTED_TYPES.includes(prevType) && UNFORMATTED_TYPES.includes(newType);
}

/**
 * Check if previous type is a TEXT and value is not a valid number
 *
 * @param prevType Previous field type
 * @param prevValue Previous field value
 * @returns True if type is TEXT with invalid number value otherwise false
 */
export function isNonConvertibleText(prevType: FieldType, prevValue: string | number | null): boolean {
    return prevType === FieldType.TEXT && prevValue !== null && isNaN(Number(prevValue));
}
